import { useFeatureFlag } from '@artic/ui/feature-flags';
import {
  AdminIcon,
  CatalogueIcon,
  DashboardsIcon,
  FactoryIcon,
  LegacyIcon,
  PlanningIcon,
  QualityIcon,
  SettingsIcon,
} from '@artic/ui/icons/nav';
import {
  getDashboardsUrl,
  getDrawingsUrl,
  getQualityUrl,
  getStorybookUrl,
} from '@artic/ui/lib/environment';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { BreadcrumbItem, BreadcrumbLink, Text } from '@chakra-ui/react';
import { QueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
} from 'react-router-dom';

import Layout from '~/routes/Layout';

import { useColumnsNavSubheader } from './hooks/useColumnsNavSubheader';
import { useImportsNavSubheader } from './hooks/useImportsNavSubheader';
import { useMaterialsNavGroup } from './hooks/useMaterialsNavGroup';
import { useOperatingCostsNavGroup } from './hooks/useOperatingCostsNavGroup';
// Import guards
import AuthenticatedProviders from './routes/guards/AuthenticatedProviders';
import RequireAuth from './routes/guards/RequireAuth';
import RequireFeatureFlag from './routes/guards/RequireFeatureFlag';
import RequireMFA from './routes/guards/RequireMFA';
import RequireSection, {
  firstReadableHubPath,
  HubNoAccessState,
  useHubSectionAccess,
} from './routes/guards/RequireSection';
import { clearChunkReloadFlag, lazyWithRetry } from './utils/lazyWithRetry';

// Lazy load pages
const ErrorPage500 = lazyWithRetry(() => import('./routes/ErrorPage500'));
const DevicesIndexPage = lazyWithRetry(
  () => import('./routes/admin_pages/devices/DevicesIndexPage'),
);
const TusksDetailsPage = lazyWithRetry(
  () => import('./routes/admin_pages/devices/TusksDetailsPage'),
);
const ProfilesIndexPage = lazyWithRetry(
  () => import('./routes/admin_pages/profiles/ProfilesIndexPage'),
);
const ProfileDetailsPage = lazyWithRetry(
  () => import('./routes/admin_pages/profiles/ProfilesDetailsPage'),
);
const ChangePasswordPage = lazyWithRetry(() => import('./routes/user_pages/ChangePasswordPage'));
const NotificationSettingsPage = lazyWithRetry(
  () => import('./routes/user_pages/NotificationSettingsPage'),
);
const ConfirmSignInPage = lazyWithRetry(() => import('./routes/auth_pages/ConfirmSignInPage'));
const UnsubscribePage = lazyWithRetry(() => import('./routes/morning_brief_pages/UnsubscribePage'));
const SubscribeConfirmationPage = lazyWithRetry(
  () => import('./routes/morning_brief_pages/SubscribeConfirmationPage'),
);
const MultiFactorAuthenticationChallengePage = lazyWithRetry(
  () => import('./routes/auth_pages/MultiFactorAuthenticationChallengePage'),
);
const LoginPage = lazyWithRetry(() => import('./routes/auth_pages/LoginPage'));
const DoctorPage = lazyWithRetry(() => import('./routes/auth_pages/DoctorPage'));
const StatusboardPage = lazyWithRetry(() => import('./routes/main_pages/statusboard'));
const WorkOrdersTablePage = lazyWithRetry(
  () => import('./routes/main_pages/schedules/WorkOrdersTablePage/WorkOrderTablePage'),
);
const WorkOrderDetailsPage = lazyWithRetry(
  () => import('./routes/main_pages/schedules/WorkOrderDetailsPage'),
);
const ProgressIndexPage = lazyWithRetry(
  () => import('./routes/main_pages/schedules/ProgressIndexPage'),
);
// ENG-775: Job Cards live in the Quality app now. Both hub routes forward there.
// `JobsIndexPage` / `JobDetailsPage` are deliberately no longer imported, so the
// Legacy modules drop out of the bundle; deleting the files is a follow-up.
const JobCardsRedirect = lazyWithRetry(
  () => import('./routes/main_pages/job-cards/JobCardsRedirect'),
);
const ProductionTargetDetailsPage = lazyWithRetry(
  () => import('./routes/main_pages/performance/TargetDetailsPage/TargetDetailsPage'),
);
const ManufacturingQualityPackIndexPage = lazyWithRetry(
  () => import('./routes/main_pages/quality/ManufacturingQualityPackIndex'),
);
const LogisticsAndInstallQualityPackIndexPage = lazyWithRetry(
  () => import('./routes/main_pages/quality/LogisticsAndInstallQualityPackIndex'),
);
const QualityLiveFeedPage = lazyWithRetry(() => import('./routes/main_pages/quality/QualityFeed'));
const VisionQualityChecksPage = lazyWithRetry(
  () => import('./routes/main_pages/quality/VisionQualityChecks'),
);
const VisionQualityCheckDetailPage = lazyWithRetry(
  () => import('./routes/main_pages/quality/VisionQualityChecks/VisionQualityCheckDetailPage'),
);
const PerformancePage = lazyWithRetry(
  () => import('./routes/main_pages/performance/PerformanceIndexPage'),
);
const WorkerEngagementIndexPage = lazyWithRetry(
  () => import('./routes/main_pages/worker-engagement/WorkerEngagementIndex'),
);
const WorkerEngagementDetailsPage = lazyWithRetry(
  () => import('./routes/main_pages/worker-engagement/WorkerEngagementDetails'),
);
const OperationsPage = lazyWithRetry(() => import('./routes/production_planning_pages/operations'));
const OperationDetailsPage = lazyWithRetry(
  () => import('./routes/production_planning_pages/operations/OperationDetailsPage'),
);
const ProductsIndexPage = lazyWithRetry(
  () => import('./routes/production_planning_pages/products/ProductsIndexPage'),
);
const ProductDetailsPage = lazyWithRetry(
  () => import('./routes/production_planning_pages/products/ProductDetailsPage'),
);
const WorkforceIndexPage = lazyWithRetry(
  () => import('./routes/production_planning_pages/workforce/WorkforceRedirect'),
);

const ChangeControlPage = lazyWithRetry(
  () => import('./routes/production_planning_pages/change-control/ChangeControlPage'),
);
const DrawingsPage = lazyWithRetry(
  () => import('./routes/production_planning_pages/drawings/DrawingsIndexPage'),
);
const MultiFactorAuthenticationEnrollPage = lazyWithRetry(
  () => import('./routes/user_pages/MultiFactorAuthenticationEnrollPage'),
);
const QualityPackDetails = lazyWithRetry(
  () => import('./routes/main_pages/quality/QualityPackDetails'),
);
const QualityCheckDetails = lazyWithRetry(
  () => import('./routes/main_pages/quality/QualityCheckDetails'),
);

const ProjectIndexPage = lazyWithRetry(
  () => import('./routes/production_planning_pages/projects/ProjectIndexPage/ProjectIndexPage'),
);

const ShiftPatternsIndexPage = lazyWithRetry(
  () => import('./routes/production_planning_pages/shift-patters/ShiftPatternsIndexPage'),
);

// Types
import { useHasFeature } from '@saas-ui-pro/feature-flags';
import { useRecoilValue } from 'recoil';
import { useDocumentTitle } from 'usehooks-ts';

import { isAdminUserCheck } from './atoms/isAdminUserCheck';
import { isEssModularUserCheck } from './atoms/isEssUserCheck';
import { isTrunkUserCheck } from './atoms/isTrunkUserCheck';
import { LinkItemProps } from './layout/SidebarLayout';
import AndonsDetailsPage from './routes/admin_pages/devices/AndonsDetailsPage';
// Import loaders
import { loader as andonLoader } from './routes/admin_pages/devices/AndonsDetailsPage/api';
import type { Andon } from './routes/admin_pages/devices/AndonsDetailsPage/api';
import DevicesSetupRedirect from './routes/admin_pages/devices/DevicesSetupRedirect';
import { loader as deviceLoader } from './routes/admin_pages/devices/TusksDetailsPage/api';
import type { Device } from './routes/admin_pages/devices/TusksDetailsPage/api';
import { loader as qualityCheckLoader } from './routes/main_pages/quality/QualityCheckDetails/api';
import type { QualityCheck } from './routes/main_pages/quality/QualityCheckDetails/api';
import { loader as qualityPackLoader } from './routes/main_pages/quality/QualityPackDetails/api';
import type { QualityPack } from './routes/main_pages/quality/QualityPackDetails/api';
import { loader as workOrderLoader } from './routes/main_pages/schedules/WorkOrderDetailsPage/api';
import type { WorkOrderDetails } from './routes/main_pages/schedules/WorkOrderDetailsPage/api';
import { loader as workerEngagementLoader } from './routes/main_pages/worker-engagement/WorkerEngagementDetails/api';
import { loader as operationLoader } from './routes/production_planning_pages/operations/OperationDetailsPage/api';
import type { OperationDetails } from './routes/production_planning_pages/operations/types';
import { loader as productLoader } from './routes/production_planning_pages/products/ProductDetailsPage/api';
import type { ProductDetails } from './routes/production_planning_pages/products/ProductDetailsPage/api';
import QualityInboxPage from './routes/quality_inbox';

const queryClient = new QueryClient();

/**
 * Catch-all landing: resolves to the same first-readable-section order as
 * `RequireSection`'s own redirect, so a user whose Factory access is off
 * cannot be bounced back onto `/statusboard` in a loop. With nothing readable
 * it shows the shared No access state in place.
 */
const HubLandingRedirect = () => {
  const isTrunkUser = useRecoilValue(isTrunkUserCheck);
  const fallbackPath = firstReadableHubPath(useHubSectionAccess(), isTrunkUser);

  return fallbackPath ? <Navigate replace to={fallbackPath} /> : <HubNoAccessState />;
};

const AppLayout = () => {
  const location = useLocation();
  const { t } = useTranslation(['translation']);
  const [title, setTitle] = useState('Artic Hub');

  // App loaded successfully — clear the chunk reload guard so future deploys
  // can trigger a fresh reload if needed.
  useEffect(() => {
    clearChunkReloadFlag();
  }, []);

  // Hooks for sidebar links
  const isAdmin = useRecoilValue(isAdminUserCheck);
  const useLegacyGrid = useFeatureFlag('use_legacy_grid');
  const drawingControl = useFeatureFlag('drawing_control');
  const qualityDashboard = useFeatureFlag('quality_dashboard');
  const downtimeDashboard = useFeatureFlag('downtime_dashboard');
  const neoWorkforce = useFeatureFlag('neo_workforce');
  const permissionsFlag = useFeatureFlag('permissions');
  const materialsNavGroup = useMaterialsNavGroup();
  const operatingCostsNavGroup = useOperatingCostsNavGroup();
  const columnsNavSubheader = useColumnsNavSubheader();
  const importsNavSubheader = useImportsNavSubheader();
  const {
    factory: canReadFactory,
    quality: canReadQuality,
    planner: canReadPlanner,
    catalog: canReadCatalog,
    settings: canReadSettings,
  } = useHubSectionAccess();

  // Each of these SPAs is its own origin in every environment. Resolution lives
  // in @artic/ui/lib/environment — the single source of truth shared with Neo,
  // Dashboards and Drawing Control's own sidebars.
  const qualityUrl = getQualityUrl();
  const drawingControlUrl = getDrawingsUrl();
  const dashboardsUrl = getDashboardsUrl();
  const storybookUrl = getStorybookUrl();

  const sidebarLinks: Array<LinkItemProps> = [
    {
      // ENG-866. Each dashboard has its own flag, both seeded admin_only.
      // Disabled rather than hidden when off, matching Drawing Control — the
      // Dashboards app is the flagged target, not a page with a legacy fallback.
      // Permissions v1: Quality needs Quality read, Downtime needs Factory
      // access; an item the user can't see is hidden, and so is the group
      // when neither is — the same rule as Neo's and the Dashboards app's own
      // sidebars.
      name: t('nav.dashboards'),
      icon: DashboardsIcon,
      hide: !canReadQuality && !canReadFactory,
      children: [
        {
          name: t('nav.quality'),
          href: `${dashboardsUrl}/quality`,
          disabled: !qualityDashboard,
          hide: !canReadQuality,
        },
        {
          name: t('nav.downtime'),
          href: `${dashboardsUrl}/downtime`,
          disabled: !downtimeDashboard,
          hide: !canReadFactory,
        },
      ],
    },
    {
      name: t('nav.factory'),
      icon: FactoryIcon,
      hide: !canReadFactory,
      children: [
        { name: t('nav.statusboard'), to: 'statusboard' },
        { name: t('nav.grid_schedules'), to: 'schedules/overview', hide: !useLegacyGrid },
        { name: t('nav.progress'), to: 'schedules/progress' },
        { name: t('nav.job_cards'), href: `${qualityUrl}/job-cards` },
      ],
    },
    {
      name: t('nav.quality'),
      icon: QualityIcon,
      hide: !canReadQuality,
      children: [
        { name: t('nav.inbox'), href: `${qualityUrl}/inbox` },
        { name: t('nav.packs'), href: `${qualityUrl}/quality-packs` },
        { name: t('nav.vision_quality_checks'), to: 'quality/vision-quality-checks' },
        { name: t('nav.vision_test_plans'), href: `${qualityUrl}/vision_test_plans` },
      ],
    },
    {
      name: t('nav.planner'),
      icon: PlanningIcon,
      hide: !canReadPlanner,
      children: [
        { name: t('nav.projects'), href: `${qualityUrl}/projects` },
        { name: t('nav.production'), href: `${qualityUrl}/production-planner` },
        { name: t('nav.change_control'), href: `${qualityUrl}/change_control` },
        neoWorkforce
          ? { name: t('nav.workforce'), href: `${qualityUrl}/workforce` }
          : { name: t('nav.workforce'), to: 'workforce' },
        {
          name: t('nav.drawing_control'),
          href: drawingControlUrl,
          disabled: !drawingControl,
        },
      ],
    },
    {
      name: t('nav.catalogue'),
      icon: CatalogueIcon,
      hide: !canReadCatalog,
      children: [
        { name: t('nav.products'), href: `${qualityUrl}/products` },
        { name: t('nav.operations'), href: `${qualityUrl}/operations` },
        { name: t('nav.run_sheets'), href: `${qualityUrl}/run_sheets` },
      ],
    },
    // ENG-1022: Neo's Materials section, in Neo's place.
    materialsNavGroup,
    // ENG-871 / ENG-1017: Neo's Operating Costs section, in Neo's place.
    operatingCostsNavGroup,
    {
      name: t('nav.settings'),
      icon: SettingsIcon,
      hide: !canReadSettings,
      children: [
        {
          kind: 'subheader',
          name: t('nav.factory'),
          children: [
            { name: t('nav.factory_admin'), href: `${qualityUrl}/factories` },
            { name: t('nav.cause_categories'), href: `${qualityUrl}/cause_categories` },
            { name: t('nav.routes'), href: `${qualityUrl}/factory-routes` },
          ],
        },
        // ENG-1035: Containers, Columns, Custom Columns and Materials.
        columnsNavSubheader,
        // ENG-1022: Work Order Imports and Material Imports.
        importsNavSubheader,
        {
          kind: 'subheader',
          name: t('nav.workforce'),
          children: [{ name: t('nav.shift-patterns'), href: `${qualityUrl}/shift-patterns` }],
        },
        {
          kind: 'subheader',
          name: t('nav.users'),
          // Neo's Permissions page (Settings read to view, write to edit);
          // the whole Settings group is already hidden without Settings read.
          children: [
            {
              name: t('nav.permissions'),
              href: `${qualityUrl}/permissions`,
              disabled: !permissionsFlag,
            },
          ],
        },
      ],
    },
    {
      name: t('nav.artic_admin'),
      icon: AdminIcon,
      hide: !isAdmin,
      children: [
        { name: t('nav.container_library'), href: `${qualityUrl}/admin/containers` },
        { name: t('nav.audit_viewer'), href: `${qualityUrl}/audit` },
        { name: t('nav.feature_flags'), href: `${qualityUrl}/admin/feature-flags` },
        { name: t('nav.partners'), href: `${qualityUrl}/admin/partners` },
        { name: t('nav.devices'), href: `${qualityUrl}/admin/devices` },
        {
          kind: 'subheader',
          name: t('nav.resources'),
          children: [
            storybookUrl
              ? { name: t('nav.storybook'), href: storybookUrl }
              : { name: t('nav.storybook'), disabled: true },
          ],
        },
      ],
    },
    {
      name: t('nav.legacy'),
      icon: LegacyIcon,
      hide: !isAdmin,
      children: [
        { name: t('nav.live_feed_quality'), to: 'feed' },
        { name: t('nav.grid_schedule'), to: 'schedules/overview' },
        { name: t('nav.profiles'), to: 'profiles' },
        { name: t('nav.products'), to: 'products' },
        { name: t('nav.operations'), to: 'operations' },
        { name: t('nav.drawings_legacy'), to: 'drawings', hide: !useLegacyGrid },
      ],
    },
  ];

  useEffect(() => {
    const getTitle = () => {
      const path = location.pathname;
      const routes = [
        { path: '/statusboard', title: t('nav.statusboard') },
        { path: '/devices', title: t('nav.devices') },
        { path: '/profiles', title: t('nav.profiles') },
        { path: '/quality', title: t('nav.quality') },
        { path: '/operations', title: t('nav.operations') },
        { path: '/products', title: t('product', { count: 0 }) },
        { path: '/schedules/progress', title: t('nav.progress') },
        { path: '/schedules', title: t('nav.schedules') },
        { path: '/job-cards', title: t('nav.job_cards') },
        { path: '/feed', title: t('nav.live_feed') },
        { path: '/performance', title: t('nav.performance_and_target_kpi') },
        { path: '/worker-engagement', title: t('nav.worker_engagement') },
        { path: '/projects', title: t('projects', { count: 0 }) },
        { path: '/drawings', title: t('nav.drawings', { count: 0 }) },
        { path: '/workforce', title: t('nav.workforce', { count: 0 }) },
        { path: '/change-control', title: t('nav.change_control') },
        { path: '/shift-patterns', title: t('nav.shift-patterns') },
        { path: '/testing', title: t('Testing') },
      ];

      const route = routes.find((route) => path.startsWith(route.path));
      setTitle(route ? `${route.title} | Artic Hub` : 'Artic Hub');
    };
    getTitle();
  }, [location.pathname, t]);

  useDocumentTitle(title);

  return <Layout sidebarLinks={sidebarLinks} />;
};

export default function AppRouter() {
  const { t } = useTranslation(['translation']);
  const { profiles } = useHasFeature('profiles');
  const isTrunkUser = useRecoilValue(isTrunkUserCheck);
  const isEssModularUser = useRecoilValue(isEssModularUserCheck);

  // Memoize the router so it's only recreated when feature flags or user
  // role change — NOT on every render. Recreating the router on every
  // render unmounts the entire route tree, losing component state and
  // potentially causing redirect loops when auth state is settling.

  const router = useMemo(
    () =>
      createBrowserRouter(
        createRoutesFromElements(
          <Route errorElement={<ErrorPage500 />}>
            <Route
              element={
                <RequireAuth>
                  <RequireMFA>
                    <AuthenticatedProviders>
                      <AppLayout />
                    </AuthenticatedProviders>
                  </RequireMFA>
                </RequireAuth>
              }
            >
              <Route
                path="statusboard"
                element={
                  <RequireSection section="factory">
                    <StatusboardPage />
                  </RequireSection>
                }
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/statusboard">
                      {t('nav.statusboard')}
                    </BreadcrumbLink>
                  ),
                }}
              />
              <Route
                path="schedules"
                element={
                  <RequireSection section="factory">
                    <Outlet />
                  </RequireSection>
                }
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/schedules">
                      {t('nav.schedules')}
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route index element={<WorkOrdersTablePage />} />
                <Route
                  path="overview"
                  handle={{
                    crumb: () => (
                      <BreadcrumbLink as={Link} to="/schedules/overview">
                        {t('nav.schedules')}
                      </BreadcrumbLink>
                    ),
                  }}
                  element={<WorkOrdersTablePage />}
                />
                <Route
                  path="progress"
                  handle={{
                    crumb: () => (
                      <BreadcrumbLink as={Link} to="/schedules/progress">
                        {t('nav.progress')}
                      </BreadcrumbLink>
                    ),
                  }}
                  element={<ProgressIndexPage />}
                />
                <Route
                  path=":workOrderId"
                  element={<WorkOrderDetailsPage />}
                  loader={workOrderLoader(queryClient)}
                  handle={{
                    crumb: (data: WorkOrderDetails) => (
                      <BreadcrumbLink isCurrentPage>{data?.work_order_name}</BreadcrumbLink>
                    ),
                  }}
                />
              </Route>
              {/* Kept as routes rather than removed so existing deep links resolve
                  and forward, instead of hitting the hub's not-found page. */}
              <Route path="job-cards">
                <Route index element={<JobCardsRedirect />} />
                <Route path=":jobCardId" element={<JobCardsRedirect />} />
              </Route>
              <Route
                path="feed"
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/feed">
                      {t('nav.live_feed')}
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route index element={<QualityLiveFeedPage />} />
              </Route>
              <Route
                path="quality"
                element={
                  <RequireSection section="quality">
                    <Outlet />
                  </RequireSection>
                }
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/quality">
                      {t('nav.quality')}
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route index element={<Navigate replace to="manufacture" />} />
                <Route path={'manufacture'} element={<ManufacturingQualityPackIndexPage />} />
                <Route
                  path={'logistics-and-install'}
                  element={
                    <RequireFeatureFlag flag={isTrunkUser}>
                      <LogisticsAndInstallQualityPackIndexPage />
                    </RequireFeatureFlag>
                  }
                />
                <Route
                  path="vision-quality-checks"
                  element={
                    <RequireFeatureFlag flag={isTrunkUser}>
                      <Outlet />
                    </RequireFeatureFlag>
                  }
                >
                  <Route index element={<VisionQualityChecksPage />} />
                  <Route path=":evidenceId" element={<VisionQualityCheckDetailPage />} />
                </Route>

                <Route
                  path=":workOrderId"
                  loader={qualityPackLoader(queryClient)}
                  handle={{
                    crumb: (data: QualityPack) => (
                      <BreadcrumbLink as={Link} to={`/quality/${data?.work_order_id}`}>
                        {data?.work_order_name}
                      </BreadcrumbLink>
                    ),
                  }}
                >
                  <Route
                    index
                    element={<QualityPackDetails />}
                    loader={qualityPackLoader(queryClient)}
                  />
                  <Route
                    path=":jobCardId/:orderNumber"
                    element={<QualityCheckDetails />}
                    loader={qualityCheckLoader(queryClient)}
                    handle={{
                      crumb: (data: QualityCheck) => (
                        <BreadcrumbLink isCurrentPage>
                          {data?.in_process_check?.name ?? 'SWI'}
                        </BreadcrumbLink>
                      ),
                    }}
                  />
                </Route>
              </Route>
              <Route
                path="performance"
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/performance">
                      {t('performance')}
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route
                  index
                  element={
                    <RequireFeatureFlag flag={isTrunkUser}>
                      <PerformancePage />
                    </RequireFeatureFlag>
                  }
                />
                <Route
                  path="productionTargetsDetails/:targetId"
                  element={
                    <RequireFeatureFlag flag={isTrunkUser}>
                      <ProductionTargetDetailsPage />
                    </RequireFeatureFlag>
                  }
                />
              </Route>
              <Route
                path="worker-engagement"
                element={
                  <RequireFeatureFlag flag={isEssModularUser || isTrunkUser}>
                    <Outlet />
                  </RequireFeatureFlag>
                }
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/worker-engagement">
                      {t('nav.worker_engagement')}
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route
                  index
                  element={
                    <RequireFeatureFlag flag={isTrunkUser}>
                      <WorkerEngagementIndexPage />
                    </RequireFeatureFlag>
                  }
                />
                <Route
                  path=":workerId/:date"
                  element={<WorkerEngagementDetailsPage />}
                  loader={workerEngagementLoader(queryClient)}
                  handle={{
                    crumb: (data: any) => (
                      <BreadcrumbItem isCurrentPage>
                        <Text>{data.workerEngagement.workers.worker_name}</Text>
                        <ChevronRightIcon color="gray.500" />
                        <Text>
                          {dayjs(data.workerEngagement?.work_date).format('dddd D MMMM, YYYY')}
                        </Text>
                      </BreadcrumbItem>
                    ),
                  }}
                />
              </Route>
              {/* Production Planning */}
              <Route
                path="operations"
                element={
                  <RequireSection section="catalog">
                    <RequireFeatureFlag flag={isTrunkUser}>
                      <Outlet />
                    </RequireFeatureFlag>
                  </RequireSection>
                }
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/operations">
                      {t('nav.operations')}
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route index element={<OperationsPage />} />
                <Route
                  path=":operationId"
                  loader={operationLoader(queryClient)}
                  handle={{
                    crumb: (data: OperationDetails) => (
                      <BreadcrumbLink
                        isCurrentPage
                        as={Link}
                        to={`/operations/${data?.operation_id}`}
                      >
                        {data?.operation_name}
                      </BreadcrumbLink>
                    ),
                  }}
                >
                  <Route
                    index
                    element={<OperationDetailsPage />}
                    loader={operationLoader(queryClient)}
                  />
                </Route>
              </Route>
              <Route
                path="projects"
                element={
                  <RequireSection section="planner">
                    <RequireFeatureFlag flag={isTrunkUser}>
                      <Outlet />
                    </RequireFeatureFlag>
                  </RequireSection>
                }
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/projects">
                      {t('projects', { count: 0 })}
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route index element={<ProjectIndexPage />} />
              </Route>
              <Route
                path="products"
                element={
                  <RequireSection section="catalog">
                    <RequireFeatureFlag flag={isTrunkUser}>
                      <Outlet />
                    </RequireFeatureFlag>
                  </RequireSection>
                }
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/products">
                      {t('product', { count: 0 })}
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route index element={<ProductsIndexPage />} />
                <Route
                  path=":runSheetId/:runSheetVersion"
                  element={<ProductDetailsPage />}
                  loader={productLoader(queryClient)}
                  handle={{
                    crumb: (data: ProductDetails) => (
                      <BreadcrumbLink isCurrentPage>
                        {data?.bill_of_operations_name} - {data?.bill_of_operations_key}
                      </BreadcrumbLink>
                    ),
                  }}
                />
                <Route
                  path=":runSheetId"
                  element={<ProductDetailsPage />}
                  loader={productLoader(queryClient)}
                  handle={{
                    crumb: (data: ProductDetails) => (
                      <BreadcrumbLink isCurrentPage>
                        {data?.bill_of_operations_name} - {data?.bill_of_operations_key}
                      </BreadcrumbLink>
                    ),
                  }}
                />
              </Route>
              <Route
                path="shift-patterns"
                element={
                  <RequireSection section="settings">
                    <RequireFeatureFlag flag={isTrunkUser}>
                      <Outlet />
                    </RequireFeatureFlag>
                  </RequireSection>
                }
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/shift-patterns">
                      {t('projects', { count: 0 })}
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route index element={<ShiftPatternsIndexPage />} />
              </Route>
              <Route
                path="workforce/*"
                element={
                  <RequireSection section="planner">
                    <WorkforceIndexPage />
                  </RequireSection>
                }
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/workforce">
                      {t('workforce', { count: 0 })}
                    </BreadcrumbLink>
                  ),
                }}
              ></Route>
              <Route
                path="change-control"
                element={
                  <RequireSection section="planner">
                    <Outlet />
                  </RequireSection>
                }
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/change-control">
                      {t('engineering_change_control', { count: 0 })}
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route index element={<ChangeControlPage />} />
              </Route>
              <Route
                path="drawings"
                element={
                  <RequireSection section="planner">
                    <Outlet />
                  </RequireSection>
                }
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/drawings">
                      {t('drawings', { count: 0 })}
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route index element={<DrawingsPage />} />
              </Route>
              {/* Admin */}
              <Route
                path="devices"
                element={
                  <DevicesSetupRedirect>
                    <RequireFeatureFlag flag={isTrunkUser}>
                      <DevicesIndexPage />
                    </RequireFeatureFlag>
                  </DevicesSetupRedirect>
                }
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/devices">
                      {t('device', { count: 0 })}
                    </BreadcrumbLink>
                  ),
                }}
              />
              <Route
                path="tusks/:deviceId"
                element={<TusksDetailsPage />}
                loader={deviceLoader(queryClient)}
                handle={{
                  crumb: (data: Device) => (
                    <BreadcrumbLink isCurrentPage>{data?.device_name}</BreadcrumbLink>
                  ),
                }}
              />
              <Route
                path="andons"
                element={
                  <RequireFeatureFlag flag={isTrunkUser}>
                    <Outlet />
                  </RequireFeatureFlag>
                }
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/devices?tabIndex=1">
                      {t('device', { count: 0 })}
                    </BreadcrumbLink>
                  ),
                }}
              >
                <Route
                  path=":hardwareId"
                  element={<AndonsDetailsPage />}
                  loader={andonLoader(queryClient)}
                  handle={{
                    crumb: (data: Andon) => (
                      <BreadcrumbLink isCurrentPage>{data?.hardware_id}</BreadcrumbLink>
                    ),
                  }}
                />
              </Route>
              <Route
                path="profiles"
                handle={{
                  crumb: () => (
                    <BreadcrumbLink as={Link} to="/profiles">
                      {t('profiles', { count: 0 })}
                    </BreadcrumbLink>
                  ),
                }}
                element={
                  <RequireFeatureFlag flag={profiles}>
                    <Outlet />
                  </RequireFeatureFlag>
                }
              >
                <Route index element={<ProfilesIndexPage />} />
                <Route path=":profileId" element={<ProfileDetailsPage />} />
              </Route>
              <Route path="change-password" element={<ChangePasswordPage />} />
              <Route path="notification-settings" element={<NotificationSettingsPage />} />
              //! THIS IS FOR LOCAL DEVELOPMENT ONLY
              {window.location.hostname === 'localhost' && (
                <Route path="inbox" element={<QualityInboxPage />} />
              )}
              <Route path="*" element={<HubLandingRedirect />} />
            </Route>
            <Route
              path="login"
              element={
                <React.Suspense>
                  <LoginPage />
                </React.Suspense>
              }
            />
            <Route
              path="doctor"
              element={
                <React.Suspense>
                  <DoctorPage />
                </React.Suspense>
              }
            />
            <Route path="confirm-signin" element={<ConfirmSignInPage />} />
            <Route path="unsubscribe" element={<UnsubscribePage />} />
            <Route path="morning-brief/subscribe" element={<SubscribeConfirmationPage />} />
            <Route path="mfa-setup" element={<MultiFactorAuthenticationEnrollPage />} />
            <Route path="mfa-challenge" element={<MultiFactorAuthenticationChallengePage />} />
          </Route>,
        ),
        {
          future: {
            v7_relativeSplatPath: true,
            v7_fetcherPersist: true,
            v7_normalizeFormMethod: true,
            v7_partialHydration: true,
            v7_skipActionErrorRevalidation: true,
          },
        },
      ),

    [isTrunkUser, isEssModularUser, profiles],
  );

  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
}
